import { Maybe } from '@monetize/types/common';
import { parseISO } from 'date-fns/parseISO';
import isString from 'lodash/isString';
import { z } from 'zod';
import { AddressRequiredSchema, AddressSchema } from './address.types';
import {
  IPaymentMethodContactSchema,
  PaymentMethodContactSchema,
} from './paymentMethod.types';

export enum RecordShareEntity {
  QUOTE = 'QUOTE',
  DEAL_ROOM = 'DEAL_ROOM',
  ACCOUNT = 'ACCOUNT',
}

export const RecordShareAccessResponseSchema = z.object({
  id: z.string(),
  url: z.string(),
  expiresAt: z.string(),
  redirectUrl: z.string().url().optional().nullable(),
});

export type RecordShareAccessResponse = z.infer<
  typeof RecordShareAccessResponseSchema
>;

export enum RecordShareEngagementAction {
  VIEWED_RECORD = 'VIEWED_RECORD',
  UPDATE_QUOTE_CONTACTS = 'UPDATE_QUOTE_CONTACTS',
  UPDATE_QUOTE_PO_NUMBER = 'UPDATE_QUOTE_PO_NUMBER',
  ADDED_PAYMENT_METHOD = 'ADDED_PAYMENT_METHOD',
  SIGNED_QUOTE = 'SIGNED_QUOTE',
  ACCEPTED_QUOTE = 'ACCEPTED_QUOTE',

  UPDATE_DEAL_ROOM_CONTACTS = 'UPDATE_DEAL_ROOM_CONTACTS',
  UPDATE_DEAL_ROOM = 'UPDATE_DEAL_ROOM',
  SIGN_DEAL_ROOM = 'SIGN_DEAL_ROOM',
  DECLINE_DEAL_ROOM = 'DECLINE_DEAL_ROOM',
}

export const RecordShareEngagementActionZ = z.enum(RecordShareEngagementAction);

export const RecordShareEngagementActionDisplay: Record<
  RecordShareEngagementAction,
  string
> = {
  VIEWED_RECORD: 'Viewed',
  UPDATE_QUOTE_CONTACTS: 'Updated Contacts',
  UPDATE_QUOTE_PO_NUMBER: 'Updated PO Number',
  ADDED_PAYMENT_METHOD: 'Added Payment Method',
  SIGNED_QUOTE: 'Signed Quote',
  ACCEPTED_QUOTE: 'Accepted Quote',

  UPDATE_DEAL_ROOM_CONTACTS: 'Updated Deal Room Contacts',
  UPDATE_DEAL_ROOM: 'Updated Deal Room',
  SIGN_DEAL_ROOM: 'Signed Deal Room',
  DECLINE_DEAL_ROOM: 'Declined Deal Room',
};

/**
 * Past-tense labels for the share activity feed. Every entry is a formatter
 * taking the shared entity's name so call sites never have to special-case the
 * entries that use it; the annotation keeps the map exhaustive over the enum.
 */
export const RecordShareEngagementActionEnglishDisplay: Record<
  RecordShareEngagementAction,
  (entity: string) => string
> = {
  VIEWED_RECORD: (entity) => `${entity} viewed`,
  UPDATE_QUOTE_CONTACTS: () => 'Contacts were updated',
  UPDATE_QUOTE_PO_NUMBER: () => 'PO number updated',
  ADDED_PAYMENT_METHOD: () => 'Payment Method added',
  SIGNED_QUOTE: () => 'Quote signed',
  ACCEPTED_QUOTE: () => 'Quote accepted',

  UPDATE_DEAL_ROOM_CONTACTS: () => 'Contacts were updated',
  UPDATE_DEAL_ROOM: () => 'Deal Room updated',
  SIGN_DEAL_ROOM: () => 'Deal Room signed',
  DECLINE_DEAL_ROOM: () => 'Deal Room declined',
};

export const RecordShareEngagementSchema = z.object({
  id: z.string(),
  action: RecordShareEngagementActionZ,
  shareId: z.string(),
  recordId: z.string(),
  email: z.string().nullish(),
  userAgent: z.string().nullish(),
  ipAddress: z.string().nullish(),
  timestamp: z
    .union([z.string(), z.date()])
    .transform((val) => (isString(val) ? parseISO(val) : val)),
});
export type RecordShareEngagement = z.infer<typeof RecordShareEngagementSchema>;

export interface RecordShareBaseParams {
  token: string;
  tenantId: string;
  recordId: string;
}

export const QuoteShareContactSchema = z.object({
  id: z.string().nullish(),
  fullName: z.string(),
  email: z.string().email(),
  phone: z.string().nullish(),
  title: z.string().nullish(),
  address: AddressSchema,
});
export type QuoteShareContact = z.infer<typeof QuoteShareContactSchema>;

export const QuoteShareContactRequiredAddressSchema =
  QuoteShareContactSchema.omit({
    address: true,
  }).extend({
    address: AddressRequiredSchema,
  });
export type QuoteShareContactRequiredAddress = z.infer<
  typeof QuoteShareContactRequiredAddressSchema
>;

const PaymentMethodRecordShareRequestSchema = z.object({
  amount: z.number().default(0),
  paymentMethodName: z.string(),
  description: z.string().optional(),
  billingDetails: PaymentMethodContactSchema,
});
export type PaymentMethodRecordShareRequest = z.infer<
  typeof PaymentMethodRecordShareRequestSchema
>;

export const QuoteShareContactsFormSchema = z
  .object({
    requireAddress: z.boolean(),
    billingContact: QuoteShareContactSchema,
    shippingContact: QuoteShareContactSchema,
    useBillingForShippingContact: z.boolean(),
  })
  .transform((val) => {
    if (val.useBillingForShippingContact) {
      val.shippingContact = {
        ...val.billingContact,
        address: {
          ...val.billingContact.address,
        },
      };
    }
    return val;
  })
  .superRefine((val, ctx) => {
    // Name is always required; the shipping contact is only validated when it is
    // not mirrored from the billing contact (its fields are hidden otherwise).
    if (!val.billingContact.fullName?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Name is required',
        path: ['billingContact', 'fullName'],
      });
    }
    if (
      !val.useBillingForShippingContact &&
      !val.shippingContact.fullName?.trim()
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Name is required',
        path: ['shippingContact', 'fullName'],
      });
    }

    if (!val.requireAddress) {
      return;
    }
    (['line1', 'city', 'postalCode', 'country'] as const).forEach((field) => {
      if (!val.billingContact.address[field]) {
        ctx.addIssue({
          code: 'custom',
          message: 'Required',
          path: ['billingContact', 'address', field],
        });
      }
      if (
        !val.useBillingForShippingContact &&
        !val.shippingContact.address[field]
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Required',
          path: ['shippingContact', 'address', field],
        });
      }
    });
  });

export type QuoteShareContactsForm = z.infer<
  typeof QuoteShareContactsFormSchema
>;

export const QuoteSharePaymentMethodFormSchema = z.object({
  paymentGatewayId: z.string().nullish(),
  paymentMethodType: z.enum(['new', 'existing']),
  paymentMethod: PaymentMethodRecordShareRequestSchema.nullish(),
  paymentMethodId: z.string().nullish(),
  paymentType: z.enum(['ELECTRONIC', 'ACH']).default('ELECTRONIC'),
});

export type QuoteSharePaymentMethodForm = z.infer<
  typeof QuoteSharePaymentMethodFormSchema
>;

export const QuoteShareFormSchema = z.object({
  config: z.object({
    requireAddress: z.boolean().default(false),
    allowPoNumber: z.boolean().default(false),
  }),
  contacts: QuoteShareContactsFormSchema,
  payment: QuoteSharePaymentMethodFormSchema,
  purchaseOrderNumber: z.string().max(255).nullish(),
});

export type QuoteShareFormInput = z.input<typeof QuoteShareFormSchema>;
export type QuoteShareForm = z.infer<typeof QuoteShareFormSchema>;

export interface QuoteShareContactRequest {
  billingContact: QuoteShareContact | QuoteShareContactRequiredAddress;
  shippingContact: QuoteShareContact | QuoteShareContactRequiredAddress;
}

export interface QuoteSharePoNumberRequest {
  purchaseOrderNumber?: Maybe<string>;
}

export interface QuoteSharePaymentMethodRequest {
  paymentGatewayId: string;
  paymentMethod: {
    amount: number;
    paymentMethodName: string;
    description?: string;
    billingDetails: IPaymentMethodContactSchema;
    paymentToken?: string;
  };
}
